import React, { Component } from 'react';
import { Footer } from '../components/Footer';
import { ShieldCheckIcon, LockIcon, KeyIcon, ServerIcon, FileTextIcon, AlertTriangleIcon, BugIcon, MailIcon, ExternalLinkIcon, CheckCircle2Icon, CircleDashedIcon, MinusCircleIcon, ClockIcon } from 'lucide-react';
type CertStatus = 'Certified' | 'In Progress' | 'Not Pursued';
const StatusBadge = ({
  status
}: {
  status: CertStatus;
}) => {
  const styles: Record<CertStatus, {
    cls: string;
    Icon: ComponentType<any>;
  }> = {
    Certified: {
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      Icon: CheckCircle2Icon
    },
    'In Progress': {
      cls: 'bg-amber-50 text-amber-700 border-amber-200',
      Icon: CircleDashedIcon
    },
    'Not Pursued': {
      cls: 'bg-stone-100 text-stone-600 border-stone-200',
      Icon: MinusCircleIcon
    }
  };
  const {
    cls,
    Icon
  } = styles[status];
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`} data-id="element-876">
      <Icon className="w-3.5 h-3.5" strokeWidth={2} data-id="element-877" />
      {status}
    </span>;
};
const SectionHeader = ({
  eyebrow,
  title,
  Icon
}: {
  eyebrow?: string;
  title: string;
  Icon?: ComponentType<any>;
}) => <div className="mb-6" data-id="element-878">
    {eyebrow && <div className="text-xs font-medium tracking-wider uppercase text-cyan-600 mb-2" data-id="element-879">
        {eyebrow}
      </div>}
    <div className="flex items-center gap-3" data-id="element-880">
      {Icon && <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-cyan-50 text-cyan-600 border border-cyan-100" data-id="element-881">
          <Icon className="w-5 h-5" strokeWidth={1.75} data-id="element-882" />
        </div>}
      <h2 className="font-serif text-2xl md:text-3xl text-stone-900" data-id="element-883">
        {title}
      </h2>
    </div>
  </div>;
const Card = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`bg-white rounded-2xl border border-stone-200 shadow-sm p-6 md:p-8 ${className}`} data-id="element-884">
    {children}
  </div>;
export const Security = () => {
  // Certifications — only include items with explicit status. No SOC 2 / ISO 27001 claims.
  const certifications: {
    name: string;
    status: CertStatus;
    note: string;
  }[] = [{
    name: 'SOC 2 Type II',
    status: 'Not Pursued',
    note: 'Available on request once we begin a formal audit cycle. Contact security@nisa.coach for our current control summary.'
  }, {
    name: 'ISO 27001',
    status: 'Not Pursued',
    note: 'Not currently on our roadmap. Equivalent controls are documented and available for review.'
  }, {
    name: 'GDPR alignment',
    status: 'In Progress',
    note: 'We honor data subject access and deletion requests today; formal documentation is being finalized.'
  }, {
    name: 'FERPA alignment',
    status: 'In Progress',
    note: 'Nisa is designed for use under district FERPA frameworks. We sign customer DPAs as a school official under direct control.'
  }];
  const subProcessors = ['Vercel', 'AWS', 'Neon', 'Upstash', 'OpenAI', 'Google AI', 'SendGrid', 'Twilio', 'Inngest', 'Langfuse', 'PostHog', 'Better Auth'];
  const documents = [{
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect personal information.',
    href: '/privacy',
    external: false
  }, {
    title: 'Terms of Service',
    description: 'The terms that govern use of Nisa.',
    href: '/terms',
    external: false
  }, {
    title: 'Sub-processors list',
    description: 'Full list available on request. Public page coming soon.',
    href: 'mailto:security@nisa.coach?subject=Sub-processors%20list',
    external: true
  }, {
    title: 'SECURITY.md',
    description: 'Full vulnerability disclosure policy and scope.',
    href: 'https://github.com/teaching-lab-ventures/nisa/blob/main/SECURITY.md',
    external: true
  }];
  return <div className="flex flex-col min-h-screen bg-stone-100" data-id="element-885">
      <main className="flex-grow" data-id="element-886">
        {/* Hero */}
        <section className="relative overflow-hidden" data-id="element-887">
          <div className="absolute inset-0 -z-10" data-id="element-888">
            <div className="absolute -top-40 -right-40 w-[700px] h-[700px] bg-cyan-200/30 rounded-full blur-3xl" data-id="element-889" />
            <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-cyan-100/40 rounded-full blur-3xl" data-id="element-890" />
          </div>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-12 md:pb-16" data-id="element-891">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-stone-200 text-stone-600 text-sm mb-6" data-id="element-892">
              <ShieldCheckIcon className="w-4 h-4 text-cyan-600" strokeWidth={1.75} data-id="element-893" />
              Security at Nisa
            </div>
            <h1 className="font-serif text-4xl md:text-6xl text-stone-900 leading-tight mb-6" data-id="element-894">
              Security at Nisa
            </h1>
            <p className="text-lg md:text-xl text-stone-600 max-w-3xl leading-relaxed" data-id="element-895">
              Coaches and the teachers they support trust Nisa with sensitive
              classroom work. We treat that data with the same care, and build
              Nisa with practices that make it straightforward for your IT and
              compliance teams to review.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 text-sm text-stone-600" data-id="element-896">
              <a href="#compliance" className="px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:border-cyan-300 hover:text-cyan-700 transition-colors" data-id="element-897">
                Compliance
              </a>
              <a href="#data-handling" className="px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:border-cyan-300 hover:text-cyan-700 transition-colors" data-id="element-898">
                Data handling
              </a>
              <a href="#encryption" className="px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:border-cyan-300 hover:text-cyan-700 transition-colors" data-id="element-899">
                Encryption
              </a>
              <a href="#access" className="px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:border-cyan-300 hover:text-cyan-700 transition-colors" data-id="element-900">
                Access
              </a>
              <a href="#disclosure" className="px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:border-cyan-300 hover:text-cyan-700 transition-colors" data-id="element-901">
                Disclosure
              </a>
            </div>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 space-y-16" data-id="element-902">
          {/* Compliance & Certifications */}
          <section id="compliance" data-id="element-903">
            <SectionHeader eyebrow="01" title="Compliance & certifications" Icon={ShieldCheckIcon} data-id="element-904" />
            <Card data-id="element-905">
              <p className="text-stone-600 mb-6" data-id="element-906">
                We list each item with its current status so reviewers can see
                exactly where we stand. We do not claim certifications we have
                not earned.
              </p>
              <ul className="divide-y divide-stone-200" data-id="element-907">
                {certifications.map(c => <li key={c.name} className="py-4 first:pt-0 last:pb-0 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-id="element-908">
                    <div className="sm:max-w-xl" data-id="element-909">
                      <div className="font-medium text-stone-900" data-id="element-910">{c.name}</div>
                      <div className="text-sm text-stone-600 mt-1 leading-relaxed" data-id="element-911">
                        {c.note}
                      </div>
                    </div>
                    <div className="shrink-0" data-id="element-912">
                      <StatusBadge status={c.status} data-id="element-913" />
                    </div>
                  </li>)}
              </ul>
            </Card>
          </section>

          {/* Data Handling */}
          <section id="data-handling" data-id="element-914">
            <SectionHeader eyebrow="02" title="Data handling" Icon={ServerIcon} data-id="element-915" />
            <Card data-id="element-916">
              <div className="grid md:grid-cols-2 gap-6" data-id="element-917">
                <div data-id="element-918">
                  <h3 className="font-serif text-lg text-stone-900 mb-2" data-id="element-919">
                    What we collect
                  </h3>
                  <p className="text-stone-600 leading-relaxed" data-id="element-920">
                    Coach and administrator account details, coaching notes and
                    artifacts uploaded by coaches, and product usage data that
                    helps us improve the experience.
                  </p>
                </div>
                <div data-id="element-921">
                  <h3 className="font-serif text-lg text-stone-900 mb-2" data-id="element-922">
                    What we don't collect
                  </h3>
                  <p className="text-stone-600 leading-relaxed" data-id="element-923">
                    We do not require, request, or store student PII. We ask
                    coaches to keep student identifiers out of uploaded notes,
                    and we provide guidance in-product to support that.
                  </p>
                </div>
                <div data-id="element-924">
                  <h3 className="font-serif text-lg text-stone-900 mb-2" data-id="element-925">
                    Where it's stored
                  </h3>
                  <p className="text-stone-600 leading-relaxed" data-id="element-926">
                    Customer data is stored in U.S.-based managed cloud services
                    (AWS and Neon), with logical isolation per organization.
                    Access is restricted to a small set of named engineers.
                  </p>
                </div>
                <div data-id="element-927">
                  <h3 className="font-serif text-lg text-stone-900 mb-2" data-id="element-928">
                    Retention
                  </h3>
                  <p className="text-stone-600 leading-relaxed" data-id="element-929">
                    We retain customer data for the life of the account.
                    Customers may request export or deletion at any time; we
                    complete verified deletion requests within 30 days.
                  </p>
                </div>
              </div>
            </Card>
          </section>

          {/* Encryption */}
          <section id="encryption" data-id="element-930">
            <SectionHeader eyebrow="03" title="Encryption" Icon={LockIcon} data-id="element-931" />
            <Card data-id="element-932">
              <div className="grid md:grid-cols-2 gap-6" data-id="element-933">
                <div className="flex gap-4" data-id="element-934">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-cyan-50 text-cyan-600 border border-cyan-100 flex items-center justify-center" data-id="element-935">
                    <LockIcon className="w-5 h-5" strokeWidth={1.75} data-id="element-936" />
                  </div>
                  <div data-id="element-937">
                    <div className="font-medium text-stone-900 mb-1" data-id="element-938">
                      In transit
                    </div>
                    <p className="text-stone-600 leading-relaxed" data-id="element-939">
                      All connections to Nisa use TLS 1.2 or higher. HTTPS is
                      enforced end-to-end across the application and APIs.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-940">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-cyan-50 text-cyan-600 border border-cyan-100 flex items-center justify-center" data-id="element-941">
                    <ServerIcon className="w-5 h-5" strokeWidth={1.75} data-id="element-942" />
                  </div>
                  <div data-id="element-943">
                    <div className="font-medium text-stone-900 mb-1" data-id="element-944">
                      At rest
                    </div>
                    <p className="text-stone-600 leading-relaxed" data-id="element-945">
                      Data at rest is encrypted with AES-256 using
                      managed-service envelope encryption across our database
                      and object storage.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </section>

          {/* Authentication & Access */}
          <section id="access" data-id="element-946">
            <SectionHeader eyebrow="04" title="Authentication & access" Icon={KeyIcon} data-id="element-947" />
            <Card data-id="element-948">
              <ul className="space-y-4" data-id="element-949">
                <li className="flex gap-3" data-id="element-950">
                  <CheckCircle2Icon className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-951" />
                  <div data-id="element-952">
                    <span className="font-medium text-stone-900" data-id="element-953">
                      Google OAuth
                    </span>{' '}
                    <span className="text-stone-600" data-id="element-954">
                      for individual coach accounts, with no Nisa-managed
                      passwords by default.
                    </span>
                  </div>
                </li>
                <li className="flex gap-3" data-id="element-955">
                  <CheckCircle2Icon className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-956" />
                  <div data-id="element-957">
                    <span className="font-medium text-stone-900" data-id="element-958">SAML SSO</span>{' '}
                    <span className="text-stone-600" data-id="element-959">
                      available for school and district plans, with IdP-managed
                      provisioning and deprovisioning.
                    </span>
                  </div>
                </li>
                <li className="flex gap-3" data-id="element-960">
                  <CheckCircle2Icon className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-961" />
                  <div data-id="element-962">
                    <span className="font-medium text-stone-900" data-id="element-963">
                      Role-based access
                    </span>{' '}
                    <span className="text-stone-600" data-id="element-964">
                      separates coach, school leader, and district admin
                      permissions so each role sees only what it needs.
                    </span>
                  </div>
                </li>
                <li className="flex gap-3" data-id="element-965">
                  <CheckCircle2Icon className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-966" />
                  <div data-id="element-967">
                    <span className="font-medium text-stone-900" data-id="element-968">
                      Least-privilege internal access
                    </span>{' '}
                    <span className="text-stone-600" data-id="element-969">
                      production access is limited to a small, named engineering
                      group and reviewed on a regular cadence.
                    </span>
                  </div>
                </li>
              </ul>
            </Card>
          </section>

          {/* Sub-processors */}
          <section id="sub-processors" data-id="element-970">
            <SectionHeader eyebrow="05" title="Sub-processors" Icon={ServerIcon} data-id="element-971" />
            <Card data-id="element-972">
              <p className="text-stone-600 leading-relaxed mb-4" data-id="element-973">
                Nisa relies on a focused set of service providers to operate the
                product. We maintain a current list of these sub-processors and
                the data each one handles.
              </p>
              <div className="flex flex-wrap gap-2 mb-6" data-id="element-974">
                {subProcessors.map(s => <span key={s} className="px-3 py-1 rounded-full text-sm bg-stone-50 text-stone-700 border border-stone-200" data-id="element-975">
                    {s}
                  </span>)}
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 text-sm text-stone-600" data-id="element-976">
                Full sub-processor list available on request to{' '}
                <a href="mailto:security@nisa.coach" className="text-cyan-600 hover:text-cyan-700 underline underline-offset-2" data-id="element-977">
                  security@nisa.coach
                </a>
                .
              </div>
            </Card>
          </section>

          {/* Penetration Testing */}
          <section id="pen-testing" data-id="element-978">
            <SectionHeader eyebrow="06" title="Penetration testing" Icon={ShieldCheckIcon} data-id="element-979" />
            <Card data-id="element-980">
              <p className="text-stone-600 leading-relaxed mb-4" data-id="element-981">
                Independent penetration testing is part of our security roadmap.
                We will publish a summary attestation here once the current
                assessment cycle completes.
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-sm" data-id="element-982">
                <ClockIcon className="w-4 h-4" strokeWidth={1.75} data-id="element-983" />
                Details to be added once assessment completes
              </div>
            </Card>
          </section>

          {/* Incident Response */}
          <section id="incident" data-id="element-984">
            <SectionHeader eyebrow="07" title="Incident response" Icon={AlertTriangleIcon} data-id="element-985" />
            <Card data-id="element-986">
              <div className="space-y-5" data-id="element-987">
                <div className="flex gap-4" data-id="element-988">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-cyan-50 text-cyan-600 border border-cyan-100 flex items-center justify-center font-medium" data-id="element-989">
                    1
                  </div>
                  <div data-id="element-990">
                    <div className="font-medium text-stone-900 mb-1" data-id="element-991">
                      Triage SLA
                    </div>
                    <p className="text-stone-600 leading-relaxed" data-id="element-992">
                      We triage suspected security incidents within 24 hours of
                      detection, with an on-call engineer assigned and a working
                      assessment of impact.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-993">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-cyan-50 text-cyan-600 border border-cyan-100 flex items-center justify-center font-medium" data-id="element-994">
                    2
                  </div>
                  <div data-id="element-995">
                    <div className="font-medium text-stone-900 mb-1" data-id="element-996">
                      Customer notification
                    </div>
                    <p className="text-stone-600 leading-relaxed" data-id="element-997">
                      Customers affected by a confirmed incident are notified
                      via their primary admin contact, with the facts we have at
                      the time and an explicit commitment to follow-up updates
                      as we learn more.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-998">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-cyan-50 text-cyan-600 border border-cyan-100 flex items-center justify-center font-medium" data-id="element-999">
                    3
                  </div>
                  <div data-id="element-1000">
                    <div className="font-medium text-stone-900 mb-1" data-id="element-1001">
                      Material incident reporting
                    </div>
                    <p className="text-stone-600 leading-relaxed" data-id="element-1002">
                      Material incidents — those involving confirmed
                      unauthorized access to customer data — are reported to
                      affected customers within 72 hours of confirmation.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </section>

          {/* Vulnerability Disclosure */}
          <section id="disclosure" data-id="element-1003">
            <SectionHeader eyebrow="08" title="Vulnerability disclosure" Icon={BugIcon} data-id="element-1004" />
            <Card data-id="element-1005">
              <p className="text-stone-600 leading-relaxed mb-4" data-id="element-1006">
                We welcome reports from security researchers, customers, and the
                broader community. We ask researchers to report suspected issues
                privately to{' '}
                <a href="mailto:security@nisa.coach" className="text-cyan-600 hover:text-cyan-700 underline underline-offset-2" data-id="element-1007">
                  security@nisa.coach
                </a>{' '}
                with steps to reproduce, and to give us a reasonable window to
                fix the issue before public disclosure.
              </p>
              <p className="text-stone-600 leading-relaxed mb-4" data-id="element-1008">
                In return, we commit to an initial response within 7 business
                days, status updates at least every 14 days while a report is
                open, and a coordinated public disclosure timeline agreed with
                the reporter. Researchers who act in good faith under this
                policy are covered by our safe-harbor language.
              </p>
              <a href="https://github.com/teaching-lab-ventures/nisa/blob/main/SECURITY.md" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-medium" data-id="element-1009">
                Read the full SECURITY.md on GitHub
                <ExternalLinkIcon className="w-4 h-4" strokeWidth={1.75} data-id="element-1010" />
              </a>
            </Card>
          </section>

          {/* Documents & Resources */}
          <section id="documents" data-id="element-1011">
            <SectionHeader eyebrow="09" title="Documents & resources" Icon={FileTextIcon} data-id="element-1012" />
            <div className="grid md:grid-cols-2 gap-4" data-id="element-1013">
              {documents.map(doc => <a key={doc.title} href={doc.href} target={doc.external ? '_blank' : undefined} rel={doc.external ? 'noopener noreferrer' : undefined} className="group bg-white rounded-2xl border border-stone-200 p-6 hover:border-cyan-300 hover:shadow-sm transition-all" data-id="element-1014">
                  <div className="flex items-start justify-between gap-4" data-id="element-1015">
                    <div data-id="element-1016">
                      <div className="font-medium text-stone-900 mb-1 group-hover:text-cyan-700 transition-colors" data-id="element-1017">
                        {doc.title}
                      </div>
                      <div className="text-sm text-stone-600 leading-relaxed" data-id="element-1018">
                        {doc.description}
                      </div>
                    </div>
                    <ExternalLinkIcon className="w-4 h-4 text-stone-400 group-hover:text-cyan-600 shrink-0 mt-1 transition-colors" strokeWidth={1.75} data-id="element-1019" />
                  </div>
                </a>)}
            </div>
          </section>

          {/* Contact */}
          <section id="contact" data-id="element-1020">
            <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden" data-id="element-1021">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" data-id="element-1022" />
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" data-id="element-1023" />
              <div className="relative" data-id="element-1024">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/15 mb-5" data-id="element-1025">
                  <MailIcon className="w-6 h-6" strokeWidth={1.75} data-id="element-1026" />
                </div>
                <h2 className="font-serif text-3xl md:text-4xl mb-3" data-id="element-1027">
                  Talk to our security team
                </h2>
                <p className="text-cyan-50 max-w-2xl mb-6 leading-relaxed" data-id="element-1028">
                  Security questionnaires, DPAs, sub-processor lists, and
                  vulnerability reports all reach us at the same address. We
                  route your request to the right person.
                </p>
                <a href="mailto:security@nisa.coach" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-cyan-700 font-medium hover:bg-cyan-50 transition-colors" data-id="element-1029">
                  <MailIcon className="w-4 h-4" strokeWidth={2} data-id="element-1030" />
                  security@nisa.coach
                </a>
              </div>
            </div>
            <p className="text-sm text-stone-500 mt-6 text-center" data-id="element-1031">
              Last updated: May 2026
            </p>
          </section>
        </div>
      </main>
      <Footer data-id="element-1032" />
    </div>;
};